import { type Material, Mesh, type Object3D, type Texture } from 'three';

const TEXTURE_KEYS = [
	'map',
	'alphaMap',
	'aoMap',
	'bumpMap',
	'displacementMap',
	'emissiveMap',
	'envMap',
	'lightMap',
	'metalnessMap',
	'normalMap',
	'roughnessMap',
	'specularMap',
	'specularIntensityMap',
	'specularColorMap',
	'clearcoatMap',
	'clearcoatNormalMap',
	'clearcoatRoughnessMap',
	'sheenColorMap',
	'sheenRoughnessMap',
	'transmissionMap',
	'thicknessMap',
	'iridescenceMap',
	'iridescenceThicknessMap',
	'anisotropyMap',
	'gradientMap',
	'matcap'
] as const;

const closeBitmapSource = (texture: Texture): void => {
	const source = texture.source as { data?: unknown } | undefined;
	const data = source?.data as { close?: () => void } | undefined;
	if (data && typeof data.close === 'function') {
		try {
			data.close();
		} catch {
			/* HTMLImageElement / non-closable sources */
		}
	}
};

export const disposeTexture = (texture: Texture | null | undefined): void => {
	if (!texture) return;
	closeBitmapSource(texture);
	texture.dispose();
};

export const disposeMaterialWithTextures = (material: Material | null | undefined): void => {
	if (!material) return;
	const slots = material as unknown as Record<string, Texture | null | undefined>;
	for (const key of TEXTURE_KEYS) {
		const tex = slots[key];
		if (tex) disposeTexture(tex);
	}
	material.dispose();
};

export const disposeObjectRecursive = (obj: Object3D): void => {
	if (!(obj instanceof Mesh)) return;
	if (obj.geometry) obj.geometry.dispose();
	const mat = obj.material as Material | Array<Material> | null | undefined;
	if (Array.isArray(mat)) {
		for (const m of mat) disposeMaterialWithTextures(m);
	} else {
		disposeMaterialWithTextures(mat);
	}
};
